<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                Create Management Fee Invoice
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Get started by filling in the information below to create a new
                                                management fee invoice.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="mt-6 grid grid-cols-2 gap-8">
                                                <div class="col-span-1">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Management Fees Information</h3>
                                                    <div class="flex flex-col mt-8">
                                                        <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                                            <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                                            <div class="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg block" style="max-height: 750px;overflow-y: scroll;">
                                                                <table class="min-w-full table-fixed divide-y divide-gray-300">
                                                                <thead class="bg-gray-50">
                                                                    <tr>
                                                                    <th scope="col" class="relative w-12 px-6 sm:w-16 sm:px-8"></th>
                                                                    <th scope="col" class="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900">Project Name</th>
                                                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Project Date</th>
                                                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Quote Name</th>
                                                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Service Line Name</th>
                                                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody class="divide-y divide-gray-200 bg-white">
                                                                    <tr v-for="line in management_fee_service_lines" :key="line.id" :class="[selectedLines.includes(line.id) && 'bg-gray-50']">
                                                                    <td class="relative w-12 px-6 sm:w-16 sm:px-8">
                                                                        <div v-if="selectedLines.includes(line.id)" class="absolute inset-y-0 left-0 w-0.5 bg-amaze-coral"></div>
                                                                        <input type="checkbox" class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-pitch-purple focus:ring-indigo-500 sm:left-6" :value="line.id" v-model="selectedLines" />
                                                                    </td>
                                                                    <td :class="['whitespace-nowrap py-4 pr-3 text-sm font-medium', selectedLines.includes(line.id) ? 'text-pitch-purple' : 'text-gray-900']">
                                                                        {{ line.quote.project.title }}
                                                                    </td>
                                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                        {{ moment(line.quote.project.starts_at, 'YYYY-MM-DD').format('DD-MM-YYYY') }}
                                                                    </td>
                                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                        {{ line.quote.name }}
                                                                    </td>
                                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                        {{ line.name }}
                                                                    </td>
                                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                        {{ line.management_fee_total }}
                                                                    </td>
                                                                    </tr>
                                                                </tbody>
                                                                </table>
                                                            </div>
                                                            </div>
                                                        </div>
                                                        </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Invoice Information</h3>
                                                    <div class="mt-4 grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label for="reference" class="block text-sm text-gray-900">
                                                                Reference
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="text" id="reference" v-model="form.reference"
                                                                    class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                    :class="{'border-red-500': form.errors.reference}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label for="amount" class="block text-sm text-gray-900">
                                                                Amount
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="text" id="amount" v-model="form.amount" disabled="disabled"
                                                                    class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                    :class="{'border-red-500': form.errors.amount}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label for="invoiced_at" class="block text-sm text-gray-900">
                                                                Invoice Date
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="date" id="invoiced_at" v-model="form.invoiced_at"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{'border-red-500': form.errors.invoiced_at}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label for="due_at" class="block text-sm text-gray-900">
                                                                Due Date
                                                            </label>
                                                            <div class="mt-1">
                                                                <input type="date" id="due_at" v-model="form.due_at"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{'border-red-500': form.errors.due_at}"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div class="col-span-2">
                                                            <label for="payment_instructions" class="block text-sm text-gray-900">
                                                                Payment Instructions
                                                            </label>
                                                            <div class="mt-1">
                                                                <quill-editor content-type="html"
                                                                    v-model:content="form.payment_instructions"
                                                                    :class="{'border-red-500': form.errors.payment_instructions}"
                                                                    class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                                    theme="snow"></quill-editor>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="button"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-200"
                                            @click="previewInvoice()">
                                        <span>Preview</span>
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {useForm} from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import {emitter} from "@/eventHub";
import moment from "moment/moment";

export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        XIcon
    },
    setup(prop, context) {

        const show = ref(false)

        const showErrors = ref(false)

        const form = useForm({
            id: null,
            organisation_id: null,
            reference: null,
            amount: 0,
            invoiced_at: null,
            due_at: null,
            payment_instructions: null,
            quote_service_ids: []
        });

        function close() {
            this.form.reset();
            this.form.clearErrors();
            this.management_fee_service_lines = [];
            this.selectedLines = [];
            this.show = false;
        }

        function open() {
            this.getConfig();
            this.form.clearErrors();
            this.show = true;
        }

        const emitter = emitter;

        return {
            show,
            form,
            close,
            open,
            showErrors,
            emitter,
        }
    },
    mounted() {
        emitter.on('openManagementFeeInvoiceSlideout', (organisation) => {
            if (typeof organisation !== 'undefined') {
                this.form.organisation_id = organisation.id
            }
            this.open();
        });
    },
    data () {
        return {
            management_fee_service_lines: false,
            selectedLines: []
        }
    },
    methods: {
        /**
         * Get and set the new invoice config
         */
        getConfig() {
            axios.get(route('frontend.management-fees.organisation.new-invoice-config',[this.form.organisation_id])).then(result => {
                // if we don't have any management fee lines to invoice, close the modal and display an error
                if(!Object.values(result.data.invoiceable_management_fee_lines).length) {
                    this.close();
                    this.$swal.fire('No Management Fees!', 'There are no management fees to be invoiced.', 'error');
                } else {
                    // loop over each key returned from the config json
                    for (var key in result.data) {
                        if(key == 'invoiceable_management_fee_lines') {
                            this.management_fee_service_lines = result.data.invoiceable_management_fee_lines;
                        } else {
                            // set it against our new invoice form model
                            this.form[key] = result.data[key];
                        }
                    }
                }

            });
        },
        /**
         * Calculate the total amount based on the quote services checked
         */
        calculateAmount () {
            // set base amount
            var amount = 0;

            // fetch the full models of the service lines selected
            var service_lines = Object.values(this.management_fee_service_lines).filter(mfsl => Object.values(this.selectedLines).includes(mfsl.id));

            // loop over each service line
            service_lines.forEach(function(ln){
                // add the managemnet fee total to the amount var
                amount += parseFloat(ln.management_fee_total);
            });

            // update the form
            this.form.amount = amount;
        },
        /**
         * Submit the form
         */
        submit() {
            // add the selected service ids to the form body
            this.form.quote_service_ids = Object.values(this.selectedLines);

            // perform post
            this.form.post(route('frontend.management-fees.organisation.store', [this.form.organisation_id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                    this.management_fee_service_lines = [];
                    this.selectedLines = [];
                },
                onError: errors => {
                    this.showErrors = true;
                }
            });
        },
        /**
         * Preview the invoice and force a download
         */
        previewInvoice() {
            // add the selected service ids to the form body
            this.form.quote_service_ids = Object.values(this.selectedLines);

            axios({
                url: route('frontend.management-fees.organisation.preview', [this.form.organisation_id]),
                method: "POST",
                data: this.form,
                responseType: 'arraybuffer'
            }).then(r => {
                const path = window.URL.createObjectURL(new Blob([r.data]));
                const link = document.createElement('a');
                link.href = path;
                link.setAttribute('download', 'DRAFT-' + this.form.reference + '.pdf');
                document.body.appendChild(link);
                link.click(); // force download file
            });
        }
    },
    created() {
        this.moment = moment;
    },
    watch: {
        /**
         * Run calculations when selected lines updated
         */
        selectedLines () {
            this.calculateAmount();
        }
    }
}
</script>
